import { useDispatch, useSelector, useStore } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
	appError as appErrorAction,
	clearDB as clearDBAction,
	exportDB as exportDBAction,
	getCurrentEventSync as getCurrentEventSyncAction,
	getGameAccess as getGameAccessAction,
	goOnline as goOnlineAction,
	importDB as importDBAction,
	initAppData as initAppDataAction,
	redirect as redirectAction,
	resetAndRedirect as resetAndRedirectAction,
	resetConfirmSettingSave as resetConfirmSettingSaveAction,
	setAllowEventSettingsEditing as setAllowEventSettingsEditingAction,
	setConfirmSave as setConfirmSaveAction,
	setConfirmSettingSave as setConfirmSettingSaveAction,
	setDebugData as setDebugDataAction,
	setEditPlay as setEditPlayAction,
	setSelectedDate as setSelectedDateAction,
	setSelectedSport as setSelectedSportAction
} from "./actions";
import QueryStringUtils from "../../util/query-string-utils";

const useAppContext = () => {
	const dispatch = useDispatch();
	const history = useNavigate();
	const store = useStore();

	const useAppRoutes = () => useSelector(({ app }) => app.AppRoutes);
	const useMenuItems = () => useSelector(({ app }) => app.menuItems);
	const useGameMenuItems = () => useSelector(({ app }) => app.gameMenuItems);
	const useTimeZones = () => useSelector(({ app }) => app.timeZones);
	const useGameAccess = () => useSelector(({ app }) => app.gameAccess);
	const useApiAccess = () => useSelector(({ app }) => app.apiAccess);
	const usePlayerManagement = () =>
		useSelector(({ app }) => app.playerManagement);
	const useSelectedSport = () => useSelector(({ app }) => app.selectedSport);
	const useSportAccess = () => useSelector(({ app }) => app.sportAccess);
	const useIsLoading = () => useSelector(({ app }) => app.isLoading);
	const useSelectedDate = () => useSelector(({ app }) => app.selectedDate);
	const useEditPlay = () => useSelector(({ app }) => app.editPlay);
	const useInsertPlay = () => useSelector(({ app }) => app.insertPlay);
	const useInsertType = () => useSelector(({ app }) => app.insertType);
	const useConfirmSave = () => useSelector(({ app }) => app.confirmSave);
	const useCurrentEventSync = () =>
		useSelector(({ app }) => app.currentEventSync);
	const useIsSubmitting = () => useSelector(({ app }) => app.isSubmitting);
	const useIsSyncing = () => useSelector(({ app }) => app.isSyncing);
	const useAllowEventSettingsEditing = () =>
		useSelector(({ app }) => app.allowEventSettingsEditing);
	const useConfirmSettingSave = () =>
		useSelector(({ app }) => app.confirmSettingSave);
	const useSettingChangeSummary = () =>
		useSelector(({ app }) => app.settingChangeSummary);
	const useDebugData = () => useSelector(({ app }) => app.debugData);

	const redirect = (url) => {
		redirectAction(history, url);
	};

	const goOnline = (online) => {
		goOnlineAction(online);
	};

	const exportDB = () => {
		return exportDBAction();
	};

	const importDB = (blob) => {
		return importDBAction(blob);
	};

	const clearDB = () => {
		return clearDBAction();
	};

	const initAppData = () => {
		return initAppDataAction(dispatch);
	};

	const getGameAccess = () => {
		getGameAccessAction(dispatch);
	};

	const setSelectedSport = (sport) => {
		setSelectedSportAction(sport, dispatch);
	};

	const setSelectedDate = (date) => {
		setSelectedDateAction(date, dispatch);
	};

	const setEditPlay = (editPlay) => {
		setEditPlayAction(editPlay, dispatch);
	};

	const setConfirmSave = (value) => {
		const { editPlay } = store.getState().app;
		setConfirmSaveAction(value, editPlay, dispatch);
	};

	const getCurrentEventSync = () => {
		const { currentEvent } = store.getState().events;
		if (currentEvent?.id) {
			getCurrentEventSyncAction(currentEvent.id, dispatch);
		}
	};

	const resetAndRedirect = (url) => {
		resetAndRedirectAction(url, history, dispatch);
	};

	const setAllowEventSettingsEditing = (value) => {
		setAllowEventSettingsEditingAction(value, dispatch);
	};

	const setConfirmSettingSave = (config) => {
		const { currentEvent } = store.getState().events;
		const { currentSeason } = store.getState().season;
		setConfirmSettingSaveAction(currentEvent, currentSeason, config, dispatch);
	};

	const resetConfirmSettingSave = () => {
		resetConfirmSettingSaveAction(dispatch);
	};

	const appError = () => {
		appErrorAction(dispatch);
	};

	const setDebugData = (data) => {
		// Not a component, so the switch is read off window rather than the
		// router -- same criterion either way.
		if (QueryStringUtils.isDebug(window.location.search)) {
			setDebugDataAction(data, dispatch);
		}
	};

	const state = {
		appRoutes: useAppRoutes,
		menuItems: useMenuItems,
		gameMenuItems: useGameMenuItems,
		timeZones: useTimeZones,
		gameAccess: useGameAccess,
		apiAccess: useApiAccess,
		playerManagement: usePlayerManagement,
		selectedSport: useSelectedSport,
		sportAccess: useSportAccess,
		isLoading: useIsLoading,
		selectedDate: useSelectedDate,
		editPlay: useEditPlay,
		insertPlay: useInsertPlay,
		insertType: useInsertType,
		confirmSave: useConfirmSave,
		currentEventSync: useCurrentEventSync,
		isSubmitting: useIsSubmitting,
		isSyncing: useIsSyncing,
		allowEventSettingsEditing: useAllowEventSettingsEditing,
		confirmSettingSave: useConfirmSettingSave,
		settingChangeSummary: useSettingChangeSummary,
		debugData: useDebugData
	};

	return {
		state,
		redirect,
		goOnline,
		exportDB,
		importDB,
		clearDB,
		initAppData,
		getGameAccess,
		setSelectedSport,
		setSelectedDate,
		setEditPlay,
		setConfirmSave,
		getCurrentEventSync,
		resetAndRedirect,
		setAllowEventSettingsEditing,
		setConfirmSettingSave,
		resetConfirmSettingSave,
		appError,
		setDebugData
	};
};

export { useAppContext };
